import { useState } from 'react';
import { CheckCircle2, ChevronDown, Plus, Trash2 } from 'lucide-react';
import {
    useAdminDeleteQuestionMutation,
    useAdminDeleteVideoMutation,
    useAdminUpdateVideoMutation,
} from '../../../../store/api/apiSlice';
import type { AdminTrainingVideo } from '../../../../types';
import { formatDuration } from '../../lib';
import QuestionForm from './QuestionForm';

interface VideoAdminCardProps {
    video: AdminTrainingVideo;
    courseId: string;
    index: number;
}

export default function VideoAdminCard({ video, courseId, index }: VideoAdminCardProps) {
    const [updateVideo] = useAdminUpdateVideoMutation();
    const [deleteVideo] = useAdminDeleteVideoMutation();
    const [deleteQuestion] = useAdminDeleteQuestionMutation();

    const [expanded, setExpanded] = useState(false);
    const [addingQuestion, setAddingQuestion] = useState(false);

    const questions = video.questions ?? [];

    return (
        <div className="rounded-2xl border border-gray-100 bg-white">
            <div className="flex flex-wrap items-center gap-3 p-4 md:px-5">
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                    <ChevronDown
                        className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    />
                    <span className="min-w-0">
                        <span className="block truncate font-semibold text-gray-900">
                            {index + 1}. {video.title}
                        </span>
                        <span className="text-xs text-gray-500">
                            {video.sourceType === 'upload' ? 'Uploaded' : 'Link'} · {questions.length} questions
                            {formatDuration(video.durationSeconds) && ` · ${formatDuration(video.durationSeconds)}`}
                        </span>
                    </span>
                </button>

                <div className="flex items-center gap-2">
                    {video.isPublished ? (
                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                            Published
                        </span>
                    ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            Hidden
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() =>
                            updateVideo({ id: video.id, courseId, body: { isPublished: !video.isPublished } })
                        }
                        className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                        {video.isPublished ? 'Hide' : 'Publish'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (window.confirm(`Delete "${video.title}" and its questions?`)) {
                                deleteVideo({ id: video.id, courseId });
                            }
                        }}
                        aria-label="Delete lesson"
                        className="rounded-full p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-gray-100 p-4 md:p-5">
                    {video.videoUrl && (
                        <p className="mb-4 break-all text-xs text-gray-400">{video.videoUrl}</p>
                    )}

                    {/* Questions */}
                    {questions.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            No questions yet — this lesson auto-completes on watch.
                        </p>
                    ) : (
                        <ol className="space-y-3">
                            {questions.map((q, qi) => (
                                <li key={q.id} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="font-medium text-gray-900">
                                            {qi + 1}. {q.text}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (window.confirm('Delete this question?')) {
                                                    deleteQuestion({ id: q.id, courseId });
                                                }
                                            }}
                                            aria-label="Delete question"
                                            className="rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <ul className="mt-2 space-y-1">
                                        {q.options.map((o, oi) => (
                                            <li
                                                key={o.id ?? oi}
                                                className={`flex items-center gap-2 text-sm ${
                                                    o.isCorrect ? 'font-medium text-green-700' : 'text-gray-600'
                                                }`}
                                            >
                                                {o.isCorrect ? (
                                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <span className="h-4 w-4" />
                                                )}
                                                {o.text}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ol>
                    )}

                    {/* Add question */}
                    <div className="mt-4">
                        {addingQuestion ? (
                            <QuestionForm
                                videoId={video.id}
                                courseId={courseId}
                                onDone={() => setAddingQuestion(false)}
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => setAddingQuestion(true)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <Plus className="h-4 w-4 text-[#FF6B35]" /> Add question
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
